import AccountRoles from '../enums/AccountRoles';

// Role hierarchy from highest to lowest
export const ROLE_HIERARCHY = {
    [AccountRoles.ACCOUNT_OWNER]: 6,
    [AccountRoles.OWNER]: 5,
    [AccountRoles.ADMIN]: 4,
    [AccountRoles.ANALYST]: 3,
    [AccountRoles.MEMBER]: 2,
    [AccountRoles.GUEST]: 1,
    [AccountRoles.DEMO]: 0,
};

// Properties that can be edited, mapped to minimum role level required to edit them
const PROPERTY_EDIT_PERMISSIONS = {
  role_id: 3, // Admin level or higher
  first_name: 2, // Member level or higher (can edit own)
  last_name: 2, // Member level or higher (can edit own)
  gender: 2, // Member level or higher (can edit own)
  email: 4, // Owner level only
  avatar: 2, // Member level or higher (can edit own)
} as const;

type EditableProperty = keyof typeof PROPERTY_EDIT_PERMISSIONS;

/**
 * Get the hierarchy level of a role
 * @param role The role to get the level for
 * @returns The hierarchy level (1-4) or 0 if role is invalid
 */
export const getRoleLevel = (role: AccountRoles): number => {
    return ROLE_HIERARCHY[role] || 0;
};

/**
 * Check if one role is higher than another in the hierarchy
 * @param role1 The first role to compare
 * @param role2 The second role to compare
 * @returns True if role1 is higher than role2
 */
export const isRoleHigher = (role1: AccountRoles, role2: AccountRoles): boolean => {
    return getRoleLevel(role1) > getRoleLevel(role2);
};

/**
 * Check if one role is higher or equal to another in the hierarchy
 * @param role1 The first role to compare
 * @param role2 The second role to compare
 * @returns True if role1 is higher or equal to role2
 */
export const isRoleHighOrEqual = (role1: AccountRoles, role2: AccountRoles): boolean => {
    return getRoleLevel(role1) >= getRoleLevel(role2);
};

/**
 * Check if a user can edit a specific property of another user
 * @param editorRole The role of the user trying to make the edit
 * @param targetRole The role of the user being edited
 * @param property The property being edited
 * @param isSelfEdit Whether the user is editing their own properties
 * @returns True if the edit is allowed
 */
export const canEditProperty = (
    editorRole: AccountRoles,
    targetRole: AccountRoles,
    property: EditableProperty,
    isSelfEdit: boolean
): boolean => {
    const editorLevel = getRoleLevel(editorRole);
    const targetLevel = getRoleLevel(targetRole);
    const requiredLevel = PROPERTY_EDIT_PERMISSIONS[property];

    // Special cases for role editing
    if (property === 'role_id') {
        // Owners cannot change their own role
        if (isSelfEdit && editorLevel === ROLE_HIERARCHY[AccountRoles.OWNER]) {
        return false;
        }
        // Can only edit roles of users with lower role levels
        return editorLevel >= requiredLevel && editorLevel > targetLevel;
    }

    // For other properties
    if (isSelfEdit) {
        // Users can edit their own properties if they meet the minimum role requirement
        return editorLevel >= requiredLevel;
    } else {
        // Can only edit properties of users with lower role levels
        return editorLevel >= requiredLevel && editorLevel > targetLevel;
    }
};

/**
 * Check if a user can view specific properties of another user
 * @param viewerRole The role of the user trying to view
 * @param targetRole The role of the user being viewed
 * @param property The property being viewed
 * @returns True if viewing is allowed
 */
export const canViewProperty = (
    viewerRole: AccountRoles,
    targetRole: AccountRoles,
    property: EditableProperty
): boolean => {
    const viewerLevel = getRoleLevel(viewerRole);
    const targetLevel = getRoleLevel(targetRole);

    // Owners and admins can view all properties
    if (viewerLevel >= ROLE_HIERARCHY[AccountRoles.ADMIN]) {
        return true;
    }

    // Users can view non-sensitive properties of users at their level or below
    const sensitiveProperties: EditableProperty[] = ['role_id', 'email'];
    if (sensitiveProperties.includes(property)) {
        return viewerLevel > targetLevel;
    }

    return viewerLevel >= targetLevel;
};
